// Badge Fairness section (spec: .claude-product-specs/dashboard/sale-filter-dashboard-price-tag.md v2.2).
// Presentational: TrafficPerformance owns the fetch (one payload also feeds the
// received/sent card rows) and passes `data` down. Renders nothing while the dark
// rollout keeps this retailer disabled. All numbers arrive server-rounded — render
// verbatim, never recompute. Copy rules: simple English, "HandOff", no partner names.
// The story reads top to bottom: sent vs received → where that puts you (spectrum,
// with and without your filter) → why it matters → what the filter refuses,
// protects, and costs.
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Balance,
  Euro,
  ShieldOutlined,
  ShoppingCartOutlined,
  SwapHoriz,
  VisibilityOffOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import StatCard from './StatCard';

const BRAND_ORANGE = '#EB7536';

const num = (v) => (v == null ? null : v.toLocaleString('nl-NL'));
const eur = (v) => (v == null ? null : `€${v.toLocaleString('nl-NL')}`);

// Distinct zone tints: slate -> neutral -> green, matching the rank direction
// (sending more ranks you higher when there are multiple matches).
const ZONES = [
  { key: 'receivers', label: 'receives more · ranks lower', color: '#eceff1' },
  { key: 'balanced', label: 'even', color: '#dde3e6' },
  { key: 'givers', label: 'sends more · ranks higher', color: '#eceff1' },
];

// Marker labels sit centred AT their arrow — only clamped when the marker is truly at
// the edge, so a label never overflows the card.
const labelTransform = (pos) => {
  if (pos < 0.08) return 'translateX(-10%)';
  if (pos > 0.92) return 'translateX(-90%)';
  return 'translateX(-50%)';
};

/**
 * One scale, whole story: three zones (receives more / even / sends more), the cohort
 * roughly plotted as anonymous quantized dots, and up to two dot markers ON the track —
 * a filled orange "You now" (where you count: capped to even while the filter cap
 * binds, else your raw position) and, when capped, a dashed hollow circle out at the
 * raw position: "where you'd be without your sale filter", with a tinted arrow segment
 * showing the gap. Positions come from the server; no scores are shown.
 */
const BalanceSpectrum = ({ spectrum, cohort }) => {
  if (!spectrum) return null;
  const [e0, e1] = spectrum.zone_edges;
  const widths = [e0, e1 - e0, 1 - e1];
  const youNow = spectrum.capped_pos != null ? spectrum.capped_pos : spectrum.now_pos;
  const potential = spectrum.capped_pos != null ? spectrum.now_pos : null;

  // Stack dots that share a quantized bucket.
  const dotStacks = {};
  (spectrum.cohort_dots || []).forEach((p) => {
    dotStacks[p] = (dotStacks[p] || 0) + 1;
  });

  return (
    <Box sx={{ mt: 2.5 }}>
      {(!cohort || cohort.size == null) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Not enough partner shops yet to show how you compare.
        </Typography>
      )}

      {/* zone labels (rank direction inline, mockup style) */}
      <Box sx={{ display: 'flex', mb: 0.75 }}>
        {ZONES.map((z, i) => (
          <Typography
            key={z.key}
            variant="caption"
            sx={{
              width: `${widths[i] * 100}%`,
              textAlign: 'center',
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: 10.5,
              lineHeight: 1.3,
              px: 0.5,
            }}
          >
            {z.label}
          </Typography>
        ))}
      </Box>

      {/* Mockup structure (2026-07-23): dot markers ON the track — filled orange =
          "You now" (the position you count from), dashed hollow = "where you'd be
          without your sale filter" — joined by a tinted segment with a dashed arrow
          pointing at the potential. All explanation lives in the legend row below. */}
      <Box sx={{ position: 'relative', height: youNow != null && potential != null ? 64 : 44, mt: 0.5 }}>
        {/* track (uniform, with a slightly darker even band + separators) */}
        <Box
          sx={{
            position: 'absolute',
            top: 22,
            left: 0,
            right: 0,
            height: 8,
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {ZONES.map((z, i) => (
            <Box key={z.key} sx={{ width: `${widths[i] * 100}%`, bgcolor: z.color }} />
          ))}
        </Box>
        {[e0, e1].map((e) => (
          <Box
            key={e}
            sx={{
              position: 'absolute',
              top: 20,
              left: `${e * 100}%`,
              width: '1px',
              height: 12,
              bgcolor: 'grey.400',
            }}
          />
        ))}

        {/* the gap: tinted band on the track, plus an arced dashed arrow ABOVE the
            track from the hollow (click balance) dot down onto the filled (You now)
            dot — the sale filter pulling the position back */}
        {youNow != null && potential != null && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 22,
                left: `${Math.min(youNow, potential) * 100}%`,
                width: `${Math.abs(potential - youNow) * 100}%`,
                height: 8,
                bgcolor: '#F8DFC9',
              }}
            />
            <svg
              viewBox="0 0 1000 64"
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <path
                d={`M ${potential * 1000},16 Q ${((potential + youNow) / 2) * 1000},-4 ${youNow * 1000},9`}
                fill="none"
                stroke="#E58E4F"
                strokeWidth="2"
                strokeDasharray="7 6"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <Box
              sx={{
                position: 'absolute',
                top: 9,
                left: `${youNow * 100}%`,
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '8px solid #E58E4F',
              }}
            />
          </>
        )}

        {/* cohort dots on the track (stacked upward when they share a bucket) */}
        {Object.entries(dotStacks).map(([pos, count]) =>
          Array.from({ length: count }, (_, i) => (
            <Box
              key={`${pos}-${i}`}
              sx={{
                position: 'absolute',
                top: 23 - i * 8,
                left: `${parseFloat(pos) * 100}%`,
                transform: 'translateX(-50%)',
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#546e7a',
                border: '1px solid #fff',
              }}
            />
          ))
        )}

        {/* the two position markers, on the track */}
        {youNow != null && (
          <Box
            sx={{
              position: 'absolute',
              top: 19,
              left: `${youNow * 100}%`,
              transform: 'translateX(-50%)',
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: BRAND_ORANGE,
              border: '2px solid #fff',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
            }}
          />
        )}
        {potential != null && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 19,
                left: `${potential * 100}%`,
                transform: 'translateX(-50%)',
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: '#fff',
                border: '2px dashed #E58E4F',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 40,
                left: `${potential * 100}%`,
                transform: labelTransform(potential),
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              Based on your click balance
            </Typography>
          </>
        )}
      </Box>

      {/* legend row */}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: BRAND_ORANGE, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            {potential != null ? 'You now — held back by your sale filter' : 'You now'}
          </Typography>
        </Box>
        {potential != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                border: '2px dashed #E58E4F',
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Based on your click balance
            </Typography>
          </Box>
        )}
        {spectrum.cohort_dots != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#546e7a', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              {cohort && cohort.size != null
                ? `The ${cohort.size} shops you trade clicks with (rough positions)`
                : 'Partner shops (rough positions)'}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

// The "how the top spot works" explanation — one home for the copy, rendered both in
// the section's "How ranking works" expander and in the cards' contested-row tooltip.
export const TopSpotSteps = ({ filterOn }) => {
  const steps = [
    <>
      Most clicks you receive are for sizes only you have in stock. Sometimes, at least
      one other shop has that size too. Then you compete for the first spot in the
      overlay.
    </>,
    <>The first spot gets most of the clicks, so it matters.</>,
    <>
      When there is more than one shop and prices are close together (within 5%), we
      rank by balance: shops that send more shoppers than they receive go higher.
      Balance is measured over the last 30 days. Small differences do not count.
    </>,
    <>
      If a partner&apos;s rule or limit stops a shopper from seeing your shop, we still
      count that click for you. So your balance can differ a little from the clicks
      you see.
    </>,
  ];
  if (filterOn) {
    steps.push(<>Your sale filter changes your balance, and your rank as a result.</>);
  }
  return (
    <Box sx={{ p: 0.5 }}>
      {steps.map((step, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ mt: i === 0 ? 0 : 0.75 }}>
          {i + 1}. {step}
        </Typography>
      ))}
    </Box>
  );
};

// StatCard-styled side card with a caption line (the two sides of the same switch).
const SideCard = ({ icon, title, value, caption }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
        <Box sx={{ color: 'text.secondary', opacity: 0.7, display: 'flex' }}>{icon}</Box>
        <Typography color="textSecondary" variant="body2">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {caption}
      </Typography>
    </CardContent>
  </Card>
);

// "of which" breakdown inside a cost stat card: how much comes from the base sale filter
// vs the deeper discount filter. base + deeper add up to the overall stat above. Rendered
// via StatCard's `footer` slot so it sits inside the card, under the headline number.
const FilterSplit = ({ base, deeper }) => (
  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Sale filter
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {base}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Deeper discount filter
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {deeper}
      </Typography>
    </Box>
  </Box>
);

// Rendered only when the filter is ON (the parent gates it) — filter-off retailers
// never see this section, so we don't advertise a feature they might then want.
const SaleFilterBlock = ({ saleFilter, protection, impact, balanceLimit }) => {
  const [showHowBase, setShowHowBase] = useState(false);
  const [showHowDeeper, setShowHowDeeper] = useState(false);
  const deeperOn = saleFilter?.hide_deeper_discounts_when_on_sale;
  const threshold = saleFilter?.deeper_discount_threshold_pp;
  const hasImpact = impact && impact.est_missed_clicks != null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Sale filter impact
        </Typography>
        <Chip size="small" variant="outlined" label="Last 30 days" sx={{ color: 'text.secondary' }} />
      </Box>

      {/* Two filters, each with its own state + "how it works". */}
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Sale filter
          </Typography>
          <Chip size="small" label="ON" color="warning" sx={{ fontWeight: 700, letterSpacing: '0.5px' }} />
          <Box
            component="span"
            onClick={() => setShowHowBase((v) => !v)}
            sx={{
              textDecoration: 'underline dotted',
              textUnderlineOffset: '3px',
              cursor: 'pointer',
              color: 'text.secondary',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {showHowBase ? 'How it works ▴' : 'How it works ▾'}
          </Box>
        </Box>
        <Collapse in={showHowBase}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pl: 1, borderLeft: '2px solid', borderColor: 'grey.200' }}>
            <SwapHoriz sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, mt: '2px' }} />
            <Typography variant="caption" color="text.secondary">
              Shoppers on your full-price products do not see partners&apos; discounted
              offers. This works both ways: your discounted products are also hidden from
              shoppers on partners&apos; full-price products.
            </Typography>
          </Box>
        </Collapse>

        {deeperOn && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Deeper discount filter
              </Typography>
              <Chip
                size="small"
                label={threshold != null ? `ON (${Math.round(threshold)}%)` : 'ON'}
                color="warning"
                sx={{ fontWeight: 700, letterSpacing: '0.5px' }}
              />
              <Box
                component="span"
                onClick={() => setShowHowDeeper((v) => !v)}
                sx={{
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                  color: 'text.secondary',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {showHowDeeper ? 'How it works ▴' : 'How it works ▾'}
              </Box>
            </Box>
            <Collapse in={showHowDeeper}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pl: 1, borderLeft: '2px solid', borderColor: 'grey.200' }}>
                <SwapHoriz sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, mt: '2px' }} />
                <Typography variant="caption" color="text.secondary">
                  When your product is on sale and a partner offers the product with a deeper
                  discount
                  {threshold != null ? ` (> ${Math.round(threshold)} points deeper)` : ''}, they are
                  hidden. This also works both ways: when you offer a
                  {threshold != null ? ` >${Math.round(threshold)} points` : ''} deeper discount
                  compared to partners, your discounted products are hidden from their shoppers.
                </Typography>
              </Box>
            </Collapse>
          </>
        )}
      </Stack>

      {/* An outbound balance limit empties the retailer's own pages after the filter runs, and the
          engines log nothing on those calls (balance-control v1.3), so the numbers below stop
          growing. Say why, so a falling count does not read as the filter failing. */}
      {balanceLimit?.throttled && balanceLimit.side === 'outbound' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Your balance limit is on. Partners are not shown on your pages, so the sale filter has
          nothing to hide for now.
        </Typography>
      )}

      {hasImpact && (
        <>
              {/* both sides of the same switch, as cards */}
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 2, mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SideCard
                    icon={<VisibilityOffOutlined fontSize="small" />}
                    title="On partner shops"
                    value={`~${num(impact.hidden_views)}× hidden`}
                    caption={
                      <>
                        About <strong>{num(impact.est_missed_clicks)} clicks</strong> from
                        shoppers who hit a sold-out size did not reach you and you missed
                        out on orders as a result.
                      </>
                    }
                  />
                </Grid>
                {protection && protection.protected_calls != null && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SideCard
                      icon={<ShieldOutlined fontSize="small" />}
                      title="On your own pages"
                      value={`~${num(protection.protected_calls)}× protected`}
                      caption={
                        <>
                          Discounting partners hidden next to your full-price products. In
                          about <strong>{num(protection.emptied_calls)}</strong> of those,
                          the shopper saw no offer at all and the size remained sold out on
                          your site
                          {impact.own_pages_missed_clicks != null && (
                            <> (≈ {num(impact.own_pages_missed_clicks)} clicks you did not send)</>
                          )}
                          .
                        </>
                      }
                    />
                  </Grid>
                )}
              </Grid>

              {/* the cost, split by filter. The deeper-only figure is the marginal of the
                  deeper flag; the sale-filter (base) figure is the remainder. They add up
                  to the overall. Breakdown shown only when the deeper filter is on. */}
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: '1px', display: 'block', mb: 1 }}
              >
                What that cost you
              </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                {impact.est_missed_sales_eur != null && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <StatCard
                      title="Missed sales"
                      value={eur(impact.est_missed_sales_eur)}
                      icon={<Euro />}
                      color="warning.main"
                      tooltip="Our estimate, based on how often shoppers click and buy your products when they see them."
                      footer={
                        deeperOn && impact.est_deeper_only_missed_sales_eur != null ? (
                          <FilterSplit
                            base={eur(impact.est_missed_sales_eur - impact.est_deeper_only_missed_sales_eur)}
                            deeper={eur(impact.est_deeper_only_missed_sales_eur)}
                          />
                        ) : null
                      }
                    />
                  </Grid>
                )}
                {impact.est_missed_orders != null && (
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <StatCard
                      title="Missed orders"
                      value={num(impact.est_missed_orders)}
                      icon={<ShoppingCartOutlined />}
                      color="text.primary"
                      tooltip="Orders we estimate you would have received with the filter off."
                      footer={
                        deeperOn && impact.est_deeper_only_missed_orders != null ? (
                          <FilterSplit
                            base={num(impact.est_missed_orders - impact.est_deeper_only_missed_orders)}
                            deeper={num(impact.est_deeper_only_missed_orders)}
                          />
                        ) : null
                      }
                    />
                  </Grid>
                )}
                {impact.own_pages_missed_commission_eur != null && (
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <StatCard
                      title="Commission not earned"
                      value={eur(impact.own_pages_missed_commission_eur)}
                      icon={<Euro />}
                      color="text.primary"
                      tooltip="Commission on the clicks the filter stopped you sending from your own pages."
                    />
                  </Grid>
                )}
              </Grid>
        </>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        <Link component={RouterLink} to="/settings/visibility">
          Change your sale filter in Settings › Visibility.
        </Link>
      </Typography>
    </>
  );
};

// Balance Control status line (spec balance-control.md §5 D): only for a shop with a limit.
// Words: balance / limit; never "cap" or "exclude". No projected date — the gap at which it
// lifts instead. Shows the shop's OWN limit only.
// `inRow`: an active limit takes the place of the partner-rules and sale-filter notes beside the
// headline stats (it explains both: no partners on your pages, so nothing for either to hide).
const BalanceLimitLine = ({ limit, inRow = false }) => {
  if (!limit) return null;
  const gap = limit.net_30d_orders;
  const gapText =
    gap == null ? null : gap === 0 ? 'the same' : `${Math.abs(gap)} ${gap > 0 ? 'more received than sent' : 'more sent than received'}`;
  let text;
  if (limit.throttled && limit.side === 'inbound') {
    text = (
      <>
        Your balance limit is active. Shoppers on partner sites are not shown your products for now. This
        lifts by itself when the difference between orders received and orders sent is {limit.release_at} or
        less.
      </>
    );
  } else if (limit.throttled && limit.side === 'outbound') {
    text = (
      <>
        Your balance limit is active. Your shoppers are not shown partner products for now. This lifts by
        itself when the difference between orders sent and orders received is {limit.release_at} or less.
      </>
    );
  } else {
    text = <>Your balance limit is {limit.band_orders} orders. Everything is shown as normal.</>;
  }
  return (
    <Box
      sx={
        inRow
          ? { flex: 1, minWidth: 260, bgcolor: '#FFF6EC', borderRadius: 1, px: 1.5, py: 1 }
          : { mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: limit.throttled ? 'warning.50' : 'grey.50' }
      }
    >
      <Typography variant="body2" sx={{ fontWeight: limit.throttled ? 600 : 400 }}>
        {text}
      </Typography>
      {gapText && (
        <Typography variant="caption" color="text.secondary">
          Orders in the last 30 days: {gapText} ({limit.orders_received_30d ?? '—'} received, {limit.orders_sent_30d ?? '—'} sent).
        </Typography>
      )}
    </Box>
  );
};

// Ratio headline (mockup): "You send about 2× more clicks than you receive." The
// follow-up sentence is honest about EFFECTIVE standing — a capped retailer's ratio
// does NOT work in their favour, their filter neutralizes it.
const RatioHeadline = ({ sent, received, standing, capped, inGrace, graceEnds }) => {
  if (sent == null || received == null || (sent === 0 && received === 0)) return null;
  const fmtRatio = (x) => {
    const v = Math.round(x * 10) / 10;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}× more`;
  };
  let phrase;
  if (received === 0) {
    phrase = <>You send clicks and receive none yet.</>;
  } else if (sent / received >= 1.15) {
    phrase = (
      <>
        You send about <Box component="span" sx={{ color: BRAND_ORANGE }}>{fmtRatio(sent / received)}</Box>{' '}
        clicks than you receive.
      </>
    );
  } else if (received / sent >= 1.15) {
    phrase = (
      <>
        You receive about <Box component="span" sx={{ color: BRAND_ORANGE }}>{fmtRatio(received / sent)}</Box>{' '}
        clicks than you send.
      </>
    );
  } else {
    phrase = <>You send about as many clicks as you receive.</>;
  }

  let followUp;
  if (inGrace) {
    // New shops rank neutral for their first 60 days — say so, and say when it ends.
    // English ordinal date ("25th July 2026") — the UI language is English even though
    // numbers use Dutch separators.
    let startsOn = null;
    if (graceEnds) {
      const d = new Date(graceEnds);
      const day = d.getDate();
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const v = day % 100;
      const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
      startsOn = `${day}${suffix} ${d.toLocaleDateString('en-GB', { month: 'long' })} ${d.getFullYear()}`;
    }
    followUp = `Your shop is new to the network. We need 60 days of data before ranking by balance kicks in.${
      startsOn ? ` For you that starts on ${startsOn}.` : ''
    }`;
  } else if (capped) {
    followUp = 'But your sale filter is holding your rank back.';
  } else if (standing === 'giver') {
    followUp = 'That works in your favour — your shop ranks higher in the overlay when several shops match.';
  } else {
    followUp = 'Shops that send more than they receive rank higher in the overlay when several shops match.';
  }

  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography component="span" variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
        {phrase}
      </Typography>
      <Typography component="span" variant="body2" color="text.secondary">
        {followUp}
      </Typography>
    </Box>
  );
};

const HeadlineStat = ({ label, value, color }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px', fontSize: 10.5 }}
    >
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

const BadgeFairnessSection = ({ data, exceptions }) => {
  const [showSteps, setShowSteps] = useState(false);
  if (!data?.enabled) return null;

  const filterOn =
    data.sale_filter?.hide_discounted_matches_on_full_price ||
    data.sale_filter?.hide_deeper_discounts_when_on_sale;
  const limitOn = !!data.balance_limit?.throttled;

  return (
    <>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Balance sx={{ color: BRAND_ORANGE, fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Your click balance
          </Typography>
        </Box>
        <Chip size="small" variant="outlined" label="Last 30 days" sx={{ color: 'text.secondary' }} />
      </Box>
      {/* Headline: the balance as a ratio, with an honest follow-up */}
      <RatioHeadline
        sent={data.sent_30d}
        received={data.received_30d}
        standing={data.standing}
        capped={data.standing_capped}
        inGrace={data.in_grace}
        graceEnds={data.grace_ends}
      />
      {!limitOn && <BalanceLimitLine limit={data.balance_limit} />}

      {/* Sent / received / net balance — and the filter's BUT right beside it */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          columnGap: { xs: 3, sm: 5 },
          rowGap: 1.5,
          mt: 2,
          mb: 2,
        }}
      >
        <HeadlineStat label="Clicks sent" value={num(data.sent_30d)} />
        <HeadlineStat label="Clicks received" value={num(data.received_30d)} />
        {data.sent_30d != null && data.received_30d != null && (
          <HeadlineStat
            label="Net balance"
            color={BRAND_ORANGE}
            value={
              data.sent_30d === data.received_30d
                ? 'even'
                : `+${num(Math.abs(data.sent_30d - data.received_30d))} ${
                    data.sent_30d > data.received_30d ? 'sent' : 'received'
                  }`
            }
          />
        )}
        {/* Partner rules note — a rule counts toward the same balance as the sale filter
            (exception-engine §10), so the click-balance card says so and points at the section.
            Deliberately NOT "cost you": on an affected card the rule is the partner's decision,
            and attributing a loss to them is what the section copy avoids. */}
        {limitOn && <BalanceLimitLine limit={data.balance_limit} inRow />}
        {!limitOn && (exceptions?.rules || []).length > 0 && (
          <Box sx={{ flex: 1, minWidth: 260, bgcolor: '#F4F6F8', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography variant="body2">
              {(() => {
                // One card per rule that touched this retailer's pages (Phase 4b §2.6). The other
                // shop is the named partner on an own rule, the owner on another retailer's rule.
                const rules = exceptions.rules;
                const ruleCount = rules.length;
                const allAffected = rules.every((r) => !r.own);
                const names = [
                  ...new Set(rules.filter((r) => !(r.own && r.all_partners)).map((r) => (r.own ? r.partner?.name : r.owner?.name))),
                ];
                // The retailer's own all-partners rule applies to the whole network: "in place with
                // All partners" would read as a shop's name, so that case gets its own sentence.
                if (names.length === 0) {
                  return ruleCount === 1 ? (
                    <>You have a rule in place that applies to <strong>all partners</strong>. </>
                  ) : (
                    <>You have rules in place that apply to <strong>all partners</strong>. </>
                  );
                }
                const who = <strong>{names.length === 1 ? names[0] : `${names.length} partners`}</strong>;
                // Singular when it really is one rule — "rules" about a single rule reads wrong.
                if (allAffected) {
                  return ruleCount === 1 ? (
                    <>A partner rule applies between your shop and {who}. </>
                  ) : (
                    <>Partner rules apply between your shop and {who}. </>
                  );
                }
                return ruleCount === 1 ? (
                  <>You have a partner rule in place with {who}. </>
                ) : (
                  <>You have partner rules in place with {who}. </>
                );
              })()}
              You can see the{' '}
              {exceptions.rules.length === 1
                ? 'impact of this rule'
                : 'impacts of these rules'}{' '}
              in{' '}
              <Box
                component="span"
                onClick={() =>
                  document.getElementById('partner-rules')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                sx={{
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Partner rules
              </Box>{' '}
              below.
            </Typography>
          </Box>
        )}
        {!limitOn &&
          filterOn &&
          (data.impact?.est_missed_clicks > 0 || data.impact?.own_pages_missed_clicks > 0) && (
          <Box
            sx={{
              flex: 1,
              minWidth: 260,
              bgcolor: '#FFF6EC',
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography variant="body2">
              {/* Display-rounded figures can land on 0 (round_sig). "cost you about 0 clicks
                  received" is noise and undercuts the number that IS non-zero, so each clause
                  appears only when it has something to say. */}
              {data.impact.est_missed_clicks > 0 ? (
                <>
                  Your sale filter cost you about{' '}
                  <strong>{num(data.impact.est_missed_clicks)} clicks received</strong>
                  {data.impact.own_pages_missed_clicks > 0 && (
                    <>
                      {' '}and prevented <strong>{num(data.impact.own_pages_missed_clicks)} clicks sent</strong>
                    </>
                  )}
                </>
              ) : (
                <>
                  Your sale filter prevented about{' '}
                  <strong>{num(data.impact.own_pages_missed_clicks)} clicks sent</strong>
                </>
              )}
              . You can see how this impacts your{' '}
              <Box
                component="span"
                onClick={() =>
                  document
                    .getElementById('click-balance-rank')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
                sx={{
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                click rank
              </Box>{' '}
              below.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Where that puts you (and where you'd be without the filter) */}
      <Box id="click-balance-rank" sx={{ mt: 2.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Click balance rank
        </Typography>
        <Box
          component="span"
          onClick={() => setShowSteps((v) => !v)}
          sx={{
            textDecoration: 'underline dotted',
            textUnderlineOffset: '3px',
            cursor: 'pointer',
            color: 'text.secondary',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {showSteps ? 'How ranking works ▴' : 'How ranking works ▾'}
        </Box>
        <Collapse in={showSteps}>
          <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'grey.200' }}>
            <TopSpotSteps filterOn={filterOn} />
          </Box>
        </Collapse>
        <BalanceSpectrum spectrum={data.spectrum} cohort={data.cohort} />
      </Box>
      </Paper>

      {/* The filter: its own section — what it refuses, protects, and costs.
          Only shown when the filter is ON: retailers without it should not be
          promoted into asking for it. */}
      {filterOn && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
          <SaleFilterBlock
            saleFilter={data.sale_filter}
            protection={data.protection}
            impact={data.impact}
            balanceLimit={data.balance_limit}
          />
        </Paper>
      )}
    </>
  );
};

export default BadgeFairnessSection;
